import { computed, ComputedRef } from 'vue';
import { UserState } from '@/composables/User';
// eslint-disable-next-line import/no-cycle
import { TemplateEditorState as state } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { PLATFORMS_WITHOUT_PASSWORD } from './FormActionsConfig';

/**
 * Whether the active shop's e-commerce platform handles authentication itself
 * and therefore doesn't need a password field on SPM forms to create accounts.
 * Single source of truth: PLATFORMS_WITHOUT_PASSWORD in FormActionsConfig.
 */
export const isPlatformWithoutPassword = (): boolean => {
  const platform = UserState.activeShop?.solutionEcommerce?.toLowerCase() || '';
  return PLATFORMS_WITHOUT_PASSWORD.includes(platform);
};

/**
 * Whether at least one form button on the current template has the
 * `account_creation` action selected. Used to decide whether to surface the
 * "password will be ignored" warning next to a password widget config.
 */
export const hasAccountCreationActionOnTemplate = (): boolean => {
  const forms = state.template?.configuration?.forms || {};
  return Object.values(forms).some((config: any) => {
    const actions = (config?.actions_to_take || []) as string[];
    return actions.includes('account_creation');
  });
};

/**
 * Whether the current template contains at least one input-password widget.
 * Used together with isPlatformWithoutPassword to surface the warning on the
 * "Création de compte" action card.
 */
export const hasPasswordFieldOnTemplate = (): boolean => {
  const forms = state.template?.configuration?.forms || {};
  return Object.values(forms).some(
    (config: any) => config?.type === 'widget-input-password',
  );
};

/**
 * Computed conditions consumed by the declarative panel system.
 * Item configs of type `NOTICE` reference a condition by its key.
 * Add new conditions here when a future widget panel needs to surface a
 * platform- or context-dependent notice.
 */
export const useNoticeConditions = (): Record<string, ComputedRef<boolean>> => ({
  // Surfaces on the input-password widget panel when on Shopify (or any
  // password-less platform) and an account_creation action is wired on the
  // template — the collected password will be ignored at submit time.
  shopifyPasswordIgnored: computed(
    () => isPlatformWithoutPassword() && hasAccountCreationActionOnTemplate(),
  ),
});

export type NoticeConditionKey = keyof ReturnType<typeof useNoticeConditions>;

export type NoticeSeverity = 'info' | 'warning' | 'danger';
